import React from 'react';

// Utils
import { SCHEMA_TYPE_MULTI_ENUM, SCHEMA_TYPE_TEXT, SCHEMA_TYPE_YOUTUBE } from '../../util/types';
import {
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';

import SectionMultiEnum from '../../components/CustomExtendedDataSection/SectionMultiEnum';
import SectionText from '../../components/CustomExtendedDataSection/SectionText';
import SectionYoutubeVideo from '../../components/CustomExtendedDataSection/SectionYoutubeVideo';

import SectionDetailsMaybe from './SectionDetailsMaybe';

/**
 * Renders custom listing fields.
 * - SectionDetailsMaybe is used if schemaType is 'enum', 'long', or 'boolean'
 * - SectionMultiEnum is used if schemaType is 'multi-enum'
 * - SectionText is used if schemaType is 'text'
 * - SectionYoutubeVideo is used if schemaType is 'youtubeVideoUrl'
 *
 * @param {*} props include publicData, metadata, listingFieldConfigs, categoryConfiguration
 * @returns React.Fragment containing aforementioned components
 */
const CustomListingFields = props => {
  const {
    publicData,
    metadata,
    listingFieldConfigs,
    categoryConfiguration,
    skipDetailsMaybe = false,
  } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  // Category fields are shown on the search page but not on the listing page.
  // Category fields and the dimensions text field are handled elsewhere on the listing page.
  const CATEGORY_FIELD_KEYS = ['art_category', 'category_j', 'dimensions'];

  // Fields are shown by default. Set showConfig.displayOnListingPage to false to explicitly hide a field.
  const displayableFieldConfigs = listingFieldConfigs.filter(
    fieldConfig =>
      fieldConfig.showConfig?.displayOnListingPage !== false &&
      !CATEGORY_FIELD_KEYS.includes(fieldConfig.key)
  );

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };
  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      displayableFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  return (
    <>
      {!skipDetailsMaybe && (
        <SectionDetailsMaybe {...props} isFieldForCategory={isFieldForSelectedCategories} />
      )}
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnum key={key} idPrefix="listingPage" {...fieldProps} showUnselectedOptions={false} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionText key={key} {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_YOUTUBE ? (
          <SectionYoutubeVideo key={key} {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

export default CustomListingFields;
